import React, { useEffect, useState, useCallback, useRef } from "react";
import "./App.css";
// import "./ui-kit.css";
import { ReactTagManager } from "react-gtm-ts";
import debounce from "lodash/debounce";
import { ColorPicker } from "./components/ColorPicker";
import {
  ChakraProvider,
  Select,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";

declare global {
  interface Window {
    EcwidApp: any;
    Intercom: any;
    ct_$: any;
  }
}

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

interface CookieGroup {
  name: string;
  enable: boolean;
  icon: string;
  list: string[];
  blocked_url: string[];
  local_cookies_name: string[];
}

interface PopupStyle {
  position: string;
  distance: string;
  box_style: string;
  box_shape: string;
  background_color: string;
  text_color: string;
  button_shape: string;
  button_color: string;
  button_size: string;
  box_skin: string;
  gear_icon_position: string;
  gear_icon_color: string;
}

interface PopupOptions {
  parent_container: string;
  always_show: boolean;
  gear_display: boolean;
  popup_title: string;
  popup_text: string;
  accept_button_text: string;
  reject_button_text: string;
  read_button_text: string;
  read_more_link: string;
  advenced_button_text: string;
  grouped_popup: boolean;
  default_group: string;
  content_before_slider: string;
  accepted_text: string;
  declined_text: string;
  save_btn: string;
  prevent_cookies_on_document_write: boolean;
  check_country: boolean;
  countries_prefixes: string[];
  cookies_expire_time: number | string;
  cookies_path: string;
  reset_link_selector: string;
  first_party_cookies_whitelist: string[];
  third_party_cookies_whitelist: string[];
  cookies_groups_design: string;
  assets_path: string;
  video_blocked: string;
  iframe_blocked: boolean;
  cookie_popup_close_color: string;
  close_popup_text: string;
  cookies_groups_on_reject: string[];
  cookies_groups: {
    [key: string]: CookieGroup;
  };
}

interface GDPRSettings {
  popup_style: PopupStyle;
  popup_options: PopupOptions;
}

function App() {
  ReactTagManager.init(tagManagerArgs);
  const intercomInitialized = useRef(false);
  const BASE_URL = "https://wix.certifiedcode.io/ecwid-gdpr";
  const [instance, setInstance] = React.useState<any>({});
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [options, setOptions] = useState<GDPRSettings>({
    popup_style: {
      position: "bottom-panel",
      distance: "20px",
      box_style: "classic",
      box_shape: "rounded",
      background_color: "#fff584",
      text_color: "#542d04",
      button_shape: "rounded",
      button_color: "#e1e1e1",
      button_size: "normal",
      box_skin: "skin-dark-theme",
      gear_icon_position: "bottom-left",
      gear_icon_color: "#6a8ee7",
    },
    popup_options: {
      parent_container: "body",
      always_show: false,
      gear_display: true,
      popup_title: "Cookies Information",
      popup_text: "To make this site work properly, we sometimes place small data files called cookies on your device. Most big websites do this too.",
      accept_button_text: "Accept",
      reject_button_text: "Reject",
      read_button_text: "Read More",
      read_more_link: "",
      advenced_button_text: "Change Settings",
      grouped_popup: true,
      default_group: "group_2",
      content_before_slider: '<h2>Privacy settings</h2><div class="ct-ultimate-gdpr-cookie-modal-desc"><p>Decide which cookies you want to allow.</p><p>You can change these settings at any time. However, this can result in some functions no longer being available. For information on deleting the cookies, please consult your browser\'s help function.</p> <span>Learn more about the cookies we use.</span></div><h3>With the slider, you can enable or disable different types of cookies:</h3>',
      accepted_text: "This website will:",
      declined_text: "This website won't:",
      save_btn: "Save & Close",
      prevent_cookies_on_document_write: false,
      check_country: false,
      countries_prefixes: ["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "GB"],
      cookies_expire_time: 30,
      cookies_path: "/",
      reset_link_selector: ".ct-uGdpr-reset",
      first_party_cookies_whitelist: [],
      third_party_cookies_whitelist: ["https://js.stripe.com", "https://d3cy3u1txmkqs3.cloudfront.net"],
      cookies_groups_design: "skin-1",
      assets_path: "assets",
      video_blocked: "This content is blocked!",
      iframe_blocked: false,
      cookie_popup_close_color: "#fff",
      close_popup_text: "",
      cookies_groups_on_reject: ["group_1"],
      cookies_groups: {
        group_1: {
          name: "Essential",
          enable: true,
          icon: "fas fa-check",
          list: [
            "Remember your cookie permission setting",
            "Allow session cookies",
            "Gather information you input into a contact forms, newsletter and other forms across all pages",
            "Keep track of what you input in shopping cart",
            "Authenticate that you are logged into your user account",
            "Remember language version you selected",
          ],
          blocked_url: [],
          local_cookies_name: [],
        },
        group_2: {
          name: "Functionality",
          enable: true,
          icon: "fas fa-cog",
          list: [
            "Remember social media settings",
            "Remember selected region and country",
          ],
          blocked_url: [],
          local_cookies_name: [],
        },
        group_3: {
          name: "Analytics",
          enable: true,
          icon: "fas fa-chart-bar",
          list: [
            "Keep track of your visited pages and interaction taken",
            "Keep track about your location and region based on your IP number",
            "Keep track on the time spent on each page",
            "Increase the data quality of the statistics functions",
          ],
          blocked_url: [],
          local_cookies_name: [],
        },
        group_4: {
          name: "Advertising",
          enable: true,
          icon: "fas fa-exchange-alt",
          list: [
            "Use information for tailored advertising with third parties",
            "Allow you to connect to social sites",
            "Identify device you are using",
            "Gather personally identifiable information such as name and location",
          ],
          blocked_url: [],
          local_cookies_name: [],
        },
      },
    },
  });

  const toast = useToast();

  const debouncedSave = useCallback(
    debounce((newOptions: typeof options) => {
      // Only make the API call if we have a subscription
      if (!instance?.subscription) return;

      fetch(
        BASE_URL +
          "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
        {
          method: "POST",
          headers: {
            Authorization: new URLSearchParams(window.location.search).get(
              "payload"
            )!,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isEnabled,
            config: newOptions,
          }),
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.config) {
            setOptions(data.config);
            if (showPreview) {
              (window as any).ct_$("html").ultimateGDPR(data.config);
            }
            toast({
              title: "Settings updated",
              description: "Your changes have been automatically saved",
              status: "success",
              duration: 2000,
              isClosable: true,
              position: "top-right",
            });
          }
        })
        .catch((error) => {
          console.error("Error saving settings:", error);
          toast({
            title: "Error saving changes",
            description: "There was a problem saving your changes. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        });
    }, 1000),
    [isEnabled, instance?.subscription, showPreview, toast]
  );

  useEffect(() => {
    if (!intercomInitialized.current && instance.intercomToken && instance.storeProfile && instance.app) {
      const name = instance.storeProfile.account.accountName;
      const registrationDate = new Date(
        instance.storeProfile.account.registrationDate
      );
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = instance.storeProfile.company.phone;
      const email = instance.storeProfile.account.accountEmail;
      const utm_source = instance.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: instance.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
      intercomInitialized.current = true;
    }
  }, [instance]);

  useEffect(() => {
    try {
      // Initialize the application
      window.EcwidApp.init({
        app_id: "gdpr-cookie-banner",
        autoloadedflag: true,
        autoheight: false,
      });

      getInstance();

      // Function to update height
      const updateHeight = () => {
        var body = document.body,
          html = document.documentElement;

        var height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );

        window.EcwidApp.setSize({ height: height });
      };

      // Initial height update
      updateHeight();

      // Set up intervals for both instance and height updates
      const instanceIntervalId = setInterval(getInstance, 10000);
      const heightIntervalId = setInterval(updateHeight, 1000);

      // Add settings fetch after getInstance
      if (instance) {
        getSettings();
      }

      // Cleanup intervals on component unmount
      return () => {
        clearInterval(instanceIntervalId);
        clearInterval(heightIntervalId);
      };
    } catch (error) {
      console.error(error);
    }
  }, []);

  function getInstance() {
    fetch(
      BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/auth",
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setInstance(payload);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  function getSettings() {
    fetch(
      BASE_URL +
        "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
      {
        method: "GET",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        console.log('Received settings:', data);
        setIsEnabled(data.isEnabled);
        if (data.config) {
          const mergedConfig = {
            popup_style: {
              ...options.popup_style,
              ...data.config.popup_style,
            },
            popup_options: {
              ...options.popup_options,
              ...data.config.popup_options,
              cookies_groups: {
                ...options.popup_options.cookies_groups,
                ...data.config.popup_options?.cookies_groups,
              },
              third_party_cookies_whitelist: Array.from(new Set([
                ...(data.config.popup_options?.third_party_cookies_whitelist || []),
                "https://js.stripe.com",
                "https://d3cy3u1txmkqs3.cloudfront.net"
              ])),
            },
          };
          console.log('Merged config:', mergedConfig);
          setOptions(mergedConfig);
        }
      })
      .catch((error) => {
        console.error('Error fetching settings:', error);
        // On error, keep current options
        setOptions(options);
      });
  }

  const handleToggle = () => {
    if (!instance?.subscription) {
      // Open upgrade page if not subscribed
      window.open(
        instance?.storeProfile
          ? encodeURI(
              "https://www.certifiedcode.us/upgrade/apps?checkout=" +
                JSON.stringify({
                  settings: {
                    allowLogout: false,
                    allowDiscountRemoval: false,
                  },
                  items: [
                    { priceId: "pri_01jmrrt8w54an3w07rcfcwsqbc", quantity: 1 },
                  ],
                  customer: {
                    email: instance.storeProfile.account.accountEmail,
                    business: {
                      name: instance.storeProfile.account.accountName,
                    },
                    address: {
                      countryCode: instance.storeProfile.company.countryCode,
                      postalCode: instance.storeProfile.company.postalCode,
                      city: instance.storeProfile.company.city,
                    },
                  },
                  customData: { store_id: `${instance.store_id}` },
                })
            )
          : undefined,
        "_blank"
      );
    } else {
      // First update the local state
      const newEnabledState = !isEnabled;
      setIsEnabled(newEnabledState);

      // Then send the update to the server
      fetch(
        BASE_URL +
          "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
        {
          method: "POST",
          headers: {
            Authorization: new URLSearchParams(window.location.search).get(
              "payload"
            )!,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isEnabled: newEnabledState,
            config: options,
          }),
        }
      )
        .then((response) => response.json())
        .then((data) => {
          // Update state with server response
          setIsEnabled(data.isEnabled);
          if (data.config) {
            setOptions(data.config);
            if (showPreview) {
              // Use the pre-loaded script
              (window as any).ct_$("html").ultimateGDPR(data.config);
            }
          }
        })
        .catch(() => {
          // Revert on error
          setIsEnabled(!newEnabledState);
        });
    }
  };

  const handleOptionChange = (
    key: string,
    value: string | boolean | string[] | string[][] | CookieGroup
  ) => {
    console.log(key, value);
    let newOptions = { ...options };

    if (key.startsWith("style_")) {
      const field = key.substring(6); // Remove 'style_' prefix
      console.log('Style field:', field);
      if (field in newOptions.popup_style) {
        console.log('Current style value:', newOptions.popup_style[field as keyof typeof newOptions.popup_style]);
        (newOptions.popup_style as any)[field] = value;
      }
    } else if (key.startsWith("options_")) {
      const field = key.substring(8); // Remove 'options_' prefix
      if (field in newOptions.popup_options) {
        console.log('Current options value:', newOptions.popup_options[field as keyof typeof newOptions.popup_options]);
        (newOptions.popup_options as any)[field] = value;
      }
    } else if (key.startsWith("group_")) {
      const [_, groupId, ...fieldParts] = key.split("_");
      const field = fieldParts.join("_"); // Rejoin any remaining parts
      if (groupId in newOptions.popup_options.cookies_groups) {
        if (field === "enable") {
          newOptions.popup_options.cookies_groups[groupId].enable = value as boolean;
        } else if (field === "list") {
          newOptions.popup_options.cookies_groups[groupId].list = value as string[];
        } else if (field === "name") {
          newOptions.popup_options.cookies_groups[groupId].name = value as string;
        }
      }
    }

    console.log('Updated options:', newOptions);

    // Update local state immediately
    setOptions(newOptions);

    // Debounce the server update
    debouncedSave(newOptions);
  };

  useEffect(() => {
    // Cleanup debounced function on unmount
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Update preview when showPreview or options change
  useEffect(() => {
    if (showPreview && (window as any).ct_$) {
      (window as any).ct_$("html").ultimateGDPR(options);
    }
  }, [showPreview, options]);

  const handleSave = () => {
    if (!instance?.subscription) return;

    fetch(
      BASE_URL +
        "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
      {
        method: "POST",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          isEnabled,
          config: options,
        }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.config) {
          setOptions(data.config);
          if (showPreview) {
            (window as any).ct_$("html").ultimateGDPR(data.config);
          }
          toast({
            title: "Settings saved",
            description: "Your GDPR settings have been successfully updated",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        }
      })
      .catch((error) => {
        console.error("Error saving settings:", error);
        toast({
          title: "Error saving settings",
          description: "There was a problem saving your changes. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      });
  };

  return (
    <ChakraProvider>
      <div className="settings-page cf">
        <div className="settings-page__header">
          <div className="settings-page__titles settings-page__titles--left">
            <h1 className="settings-page__title">GDPR Cookie Consent Banner</h1>
            <div className="settings-page__subtitle">
              Protect your customers' privacy and comply with GDPR regulations
            </div>
          </div>

          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">
                  GDPR Compliance Overview
                </div>
              </div>
              <div className="named-area__description">
                Ensure GDPR compliance with our customizable cookie consent
                solution. Plans start from $10.00/month.
              </div>
            </div>
            <div className="named-area__body">
              <div
                className="a-card a-card--normal"
                style={{ backgroundColor: "#2964de" }}
              >
                <div className="a-card__paddings">
                  <div className="upgrade-element upgrade-element--inverted">
                    <div className="upgrade-element__core">
                      <div className="upgrade-element__data">
                        <div className="upgrade-element__title">
                          Protect Customer Privacy
                        </div>
                        <div className="upgrade-element__content">
                          <div className="upgrade-element__text">
                            Implement a fully customizable GDPR-compliant cookie
                            consent banner that lets your customers control
                            their privacy preferences.
                          </div>
                          <div className="upgrade-element__action">
                            {instance.store_id ? (
                              <div className="status-block__primary-action">
                                <label className="checkbox big">
                                  <input
                                    type="checkbox"
                                    checked={isEnabled}
                                    onChange={handleToggle}
                                  />
                                  <div>
                                    <div></div>
                                  </div>
                                </label>
                              </div>
                            ) : (
                              <div className="loader"></div>
                            )}
                          </div>
                        </div>
                        <div className="upgrade-element__note">
                          <div>
                            {instance?.subscription
                              ? "Click to toggle GDPR banner"
                              : "Upgrade to enable GDPR compliance"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Key Features</div>
              </div>
              <div className="named-area__description">
                Discover how our GDPR Cookie Consent Banner protects your
                business and customers
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div>
                    <div className="promo-row">
                      <div className="promo-row__content">
                        <ul className="titled-items-list">
                          <li className="titled-item titled-items-list__item">
                            <div className="titled-item__title">
                              Full GDPR Compliance
                            </div>
                            <div className="titled-item__content">
                              Automatically comply with GDPR requirements for
                              cookie consent with our customizable solution
                            </div>
                          </li>
                          <li className="titled-item titled-items-list__item">
                            <div className="titled-item__title">
                              Granular Cookie Control
                            </div>
                            <div className="titled-item__content">
                              Let customers choose which types of cookies they
                              accept with pre-configured categories
                            </div>
                          </li>
                          <li className="titled-item titled-items-list__item">
                            <div className="titled-item__title">
                              Seamless Integration
                            </div>
                            <div className="titled-item__content">
                              Easy setup with customizable colors, positions,
                              and text to match your brand perfectly
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-page__body">
          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Feature Summary</div>
              </div>
              <div className="named-area__description">
                Enhance your store's GDPR compliance with our customizable
                cookie consent banner. Protect your customers' privacy while
                maintaining a seamless user experience.
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="feature-element has-icon has-picture">
                    <div className="feature-element__core">
                      <div className="feature-element__data">
                        <div className="feature-element__title">
                          Enable Cookie Consent Banner
                        </div>
                        <div className="feature-element__content">
                          <div className="feature-element__text">
                            Start protecting your customers' privacy by enabling
                            the GDPR-compliant cookie consent banner. Customize
                            its appearance and behavior to match your store's
                            design.
                          </div>
                          <div className="feature-element__action">
                            <div className="toggle-row">
                              <label className="checkbox big">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={handleToggle}
                                  disabled={!instance?.subscription}
                                />
                                <div data-on="enabled" data-off="disabled">
                                  <div></div>
                                </div>
                              </label>
                              <span>Enable banner on your store</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="feature-element__icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 70 70"
                        >
                          <path d="M34.5 67h-.13c-8.68-.03-16.83-3.45-22.94-9.61C5.32 51.23 1.97 43.06 2 34.38 2.07 16.52 16.65 2 34.5 2h.13c8.68.03 16.83 3.45 22.94 9.61 6.12 6.16 9.46 14.34 9.43 23.02C66.93 52.48 52.35 67 34.5 67zm0-62C18.3 5 5.06 18.18 5 34.39c-.03 7.88 3.01 15.3 8.56 20.89 5.55 5.59 12.95 8.69 20.83 8.72h.12c16.2 0 29.44-13.18 29.5-29.39.03-7.88-3.01-15.3-8.56-20.89C49.89 8.13 42.49 5.03 34.61 5h-.11z"></path>
                          <path d="M32.17 46.67l-10.7-10.08c-.6-.57-.63-1.52-.06-2.12.57-.6 1.52-.63 2.12-.06l8.41 7.92 14.42-16.81c.54-.63 1.49-.7 2.12-.16.63.54.7 1.49.16 2.12L32.17 46.67z"></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Banner Settings</div>
              </div>
              <div className="named-area__description">
                Customize how your cookie consent banner looks and behaves
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="form-area">
                    <div className="form-area__content">
                      <div className="fieldsets-batch">
                        <div className="fieldset">
                          <div className="fieldset__title">
                            Enable Cookie Consent Banner
                          </div>
                          <div className="fieldset__content">
                            <div className="toggle-row">
                              <label className="checkbox big">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={handleToggle}
                                  disabled={!instance?.subscription}
                                />
                                <div data-on="enabled" data-off="disabled">
                                  <div></div>
                                </div>
                              </label>
                              <span>Enable banner on your store</span>
                            </div>
                          </div>
                        </div>

                        <div className="fieldset">
                          <div className="fieldset__title">Preview</div>
                          <div className="fieldset__content">
                            <div className="toggle-row">
                              <label className="checkbox big">
                                <input
                                  type="checkbox"
                                  checked={showPreview}
                                  onChange={(e) =>
                                    setShowPreview(e.target.checked)
                                  }
                                />
                                <div data-on="enabled" data-off="disabled">
                                  <div></div>
                                </div>
                              </label>
                              <span>Show Live Preview</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="form-area">
                    <div className="form-area__content">
                      <div className="fieldsets-batch">
                        <div className="fieldset">
                          <div className="fieldset__title">Style Settings</div>
                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Position</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.position}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_position",
                                  e.target.value
                                )
                              }
                            >
                              <option value="bottom-panel">Bottom Panel</option>
                              <option value="bottom-left">Bottom Left</option>
                              <option value="bottom-right">Bottom Right</option>
                              <option value="top-panel">Top Panel</option>
                              <option value="top-left">Top Left</option>
                              <option value="top-right">Top Right</option>
                            </Select>
                          </FormControl> */}

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Distance from Border
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_style.distance}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_distance",
                                  e.target.value
                                )
                              }
                              placeholder="e.g., 20px"
                            />
                          </FormControl>

                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Box Style</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.box_style}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_box_style",
                                  e.target.value
                                )
                              }
                            >
                              <option value="classic">Classic</option>
                              <option value="modern">Modern</option>
                            </Select>
                          </FormControl> */}

                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Box Shape</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.box_shape}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_box_shape",
                                  e.target.value
                                )
                              }
                            >
                              <option value="rounded">Rounded</option>
                              <option value="squared">Squared</option>
                            </Select>
                          </FormControl> */}

                          {/* <ColorPicker
                            title="Background Color"
                            value={options.popup_style.background_color}
                            onChange={(color) =>
                              handleOptionChange(
                                "style_background_color",
                                color
                              )
                            }
                          />

                          <ColorPicker
                            title="Text Color"
                            value={options.popup_style.text_color}
                            onChange={(color) =>
                              handleOptionChange("style_text_color", color)
                            }
                          /> */}

                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Button Shape</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.button_shape}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_button_shape",
                                  e.target.value
                                )
                              }
                            >
                              <option value="rounded">Rounded</option>
                              <option value="squared">Squared</option>
                            </Select>
                          </FormControl> */}

                          {/* <ColorPicker
                            title="Button Color"
                            value={options.popup_style.button_color}
                            onChange={(color) =>
                              handleOptionChange("style_button_color", color)
                            }
                          /> */}

                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Button Size</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.button_size}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_button_size",
                                  e.target.value
                                )
                              }
                            >
                              <option value="normal">Normal</option>
                              <option value="large">Large</option>
                            </Select>
                          </FormControl> */}

                          {/* <FormControl mb={4}>
                            <FormLabel fontSize="sm">Box Skin</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.box_skin}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_box_skin",
                                  e.target.value
                                )
                              }
                            >
                              <option value="skin-default-theme">
                                Default Theme
                              </option>
                              <option value="skin-dark-theme">
                                Dark Theme
                              </option>
                              <option value="skin-light-theme">
                                Light Theme
                              </option>
                            </Select>
                          </FormControl> */}

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Gear Icon Position
                            </FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_style.gear_icon_position}
                              onChange={(e) =>
                                handleOptionChange(
                                  "style_gear_icon_position",
                                  e.target.value
                                )
                              }
                            >
                              <option value="top-left">Top Left</option>
                              <option value="top-center">Top Center</option>
                              <option value="top-right">Top Right</option>
                              <option value="center-left">Center Left</option>
                              <option value="center-right">Center Right</option>
                              <option value="bottom-left">Bottom Left</option>
                              <option value="bottom-center">
                                Bottom Center
                              </option>
                              <option value="bottom-right">Bottom Right</option>
                            </Select>
                          </FormControl>

                          <ColorPicker
                            title="Gear Icon Color"
                            value={options.popup_style.gear_icon_color}
                            onChange={(color) =>
                              handleOptionChange("style_gear_icon_color", color)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="form-area">
                    <div className="form-area__content">
                      <div className="fieldsets-batch">
                        <div className="fieldset">
                          <div className="fieldset__title">
                            Content Settings
                          </div>
                          <div
                            className="fieldset__subtitle"
                            style={{ marginBottom: "16px", fontWeight: 500 }}
                          >
                            Cookie Groups
                          </div>
                          {Object.entries(
                            options.popup_options.cookies_groups
                          ).map(([groupId, group]) => (
                            <div
                              key={groupId}
                              className="cookie-group-settings"
                              style={{
                                marginBottom: "24px",
                                padding: "16px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                              }}
                            >
                              <FormControl mb={4}>
                                <FormLabel fontSize="sm">Group Name</FormLabel>
                                <Input
                                  size="sm"
                                  value={group.name}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      `group_${groupId}_name`,
                                      e.target.value
                                    )
                                  }
                                  disabled={groupId === "group_1"} // Essential group can't be renamed
                                />
                              </FormControl>

                              <FormControl mb={4}>
                                <FormLabel fontSize="sm">Enabled</FormLabel>
                                <Select
                                  size="sm"
                                  value={group.enable.toString()}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      `group_${groupId}_enable`,
                                      e.target.value === "true"
                                    )
                                  }
                                  disabled={groupId === "group_1"} // Essential group can't be disabled
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Select>
                              </FormControl>

                              <FormControl mb={4}>
                                <FormLabel fontSize="sm">
                                  Cookie Purposes
                                </FormLabel>
                                <Textarea
                                  size="sm"
                                  value={group.list.join("\n")}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      `group_${groupId}_list`,
                                      e.target.value.split("\n")
                                    )
                                  }
                                  placeholder="One purpose per line"
                                  rows={4}
                                  disabled={groupId === "group_1"} // Essential group purposes can't be modified
                                />
                              </FormControl>
                            </div>
                          ))}

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Always Show</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.always_show.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_always_show",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Show Gear Icon</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.gear_display.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_gear_display",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Title</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.popup_title}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_popup_title",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Description</FormLabel>
                            <Textarea
                              size="sm"
                              value={options.popup_options.popup_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_popup_text",
                                  e.target.value
                                )
                              }
                              rows={4}
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Accept Button Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.accept_button_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_accept_button_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Reject Button Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.reject_button_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_reject_button_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Read More Button Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.read_button_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_read_button_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Read More Link</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.read_more_link}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_read_more_link",
                                  e.target.value
                                )
                              }
                              placeholder="URL to privacy policy"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Settings Button Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.advenced_button_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_advenced_button_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Group Cookies</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.grouped_popup.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_grouped_popup",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Default Group</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.default_group}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_default_group",
                                  e.target.value
                                )
                              }
                            >
                              {Object.keys(
                                options.popup_options.cookies_groups
                              ).map((groupId) => (
                                <option key={groupId} value={groupId}>
                                  {
                                    options.popup_options.cookies_groups[
                                      groupId
                                    ].name
                                  }
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Content Before Slider
                            </FormLabel>
                            <Textarea
                              size="sm"
                              value={
                                options.popup_options.content_before_slider
                              }
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_content_before_slider",
                                  e.target.value
                                )
                              }
                              rows={6}
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Accepted Text</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.accepted_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_accepted_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Declined Text</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.declined_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_declined_text",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Save Button Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.save_btn}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_save_btn",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Prevent Cookies on Document Write
                            </FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.prevent_cookies_on_document_write.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_prevent_cookies_on_document_write",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Check Country</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.check_country.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_check_country",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Countries</FormLabel>
                            <Textarea
                              size="sm"
                              value={options.popup_options.countries_prefixes.join(
                                ", "
                              )}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_countries_prefixes",
                                  e.target.value.split(",").map((s) => s.trim())
                                )
                              }
                              placeholder="Comma-separated country codes"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Cookie Expiry (days)
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.cookies_expire_time}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_cookies_expire_time",
                                  e.target.value
                                )
                              }
                              placeholder="Number of days or 'unlimited'"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Cookies Path</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.cookies_path}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_cookies_path",
                                  e.target.value
                                )
                              }
                              placeholder="/ for global, /path for custom, or 'current'"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Reset Link Selector
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.reset_link_selector}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_reset_link_selector",
                                  e.target.value
                                )
                              }
                              placeholder="CSS selector"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              First Party Cookies Whitelist
                            </FormLabel>
                            <Textarea
                              size="sm"
                              value={options.popup_options.first_party_cookies_whitelist.join(
                                "\n"
                              )}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_first_party_cookies_whitelist",
                                  e.target.value.split("\n")
                                )
                              }
                              placeholder="One cookie name per line"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Third Party Cookies Whitelist
                            </FormLabel>
                            <Textarea
                              size="sm"
                              value={options.popup_options.third_party_cookies_whitelist.join(
                                "\n"
                              )}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_third_party_cookies_whitelist",
                                  e.target.value.split("\n")
                                )
                              }
                              placeholder="One cookie name per line"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Cookies Groups Design
                            </FormLabel>
                            <Select
                              size="sm"
                              value={
                                options.popup_options.cookies_groups_design
                              }
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_cookies_groups_design",
                                  e.target.value
                                )
                              }
                            >
                              <option value="skin-1">Skin 1</option>
                              <option value="skin-2">Skin 2</option>
                              <option value="skin-3">Skin 3</option>
                            </Select>
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Assets Path</FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.assets_path}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_assets_path",
                                  e.target.value
                                )
                              }
                              placeholder="Path to assets directory"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Video Blocked Message
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.video_blocked}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_video_blocked",
                                  e.target.value
                                )
                              }
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">Block Iframes</FormLabel>
                            <Select
                              size="sm"
                              value={options.popup_options.iframe_blocked.toString()}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_iframe_blocked",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </Select>
                          </FormControl>

                          <ColorPicker
                            title="Close Popup Color"
                            value={
                              options.popup_options.cookie_popup_close_color
                            }
                            onChange={(color) =>
                              handleOptionChange(
                                "options_cookie_popup_close_color",
                                color
                              )
                            }
                          />

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Close Popup Text
                            </FormLabel>
                            <Input
                              size="sm"
                              value={options.popup_options.close_popup_text}
                              onChange={(e) =>
                                handleOptionChange(
                                  "options_close_popup_text",
                                  e.target.value
                                )
                              }
                              placeholder="Leave empty for X button"
                            />
                          </FormControl>

                          <FormControl mb={4}>
                            <FormLabel fontSize="sm">
                              Groups to Accept on Reject
                            </FormLabel>
                            <Select
                              size="sm"
                              value={
                                options.popup_options.cookies_groups_on_reject
                              }
                              onChange={(e) => {
                                const selectedOptions = Array.from(
                                  e.target.selectedOptions,
                                  (option) => option.value
                                );
                                handleOptionChange(
                                  "options_cookies_groups_on_reject",
                                  selectedOptions
                                );
                              }}
                              multiple
                            >
                              {Object.entries(
                                options.popup_options.cookies_groups
                              ).map(([id, group]) => (
                                <option key={id} value={id}>
                                  {group.name}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="a-card a-card--normal">
          <div className="a-card__paddings">
            <div className="form-area">
              <div className="form-area__content">
                <div className="fieldsets-batch">
                  <div className="fieldset">
                    <div className="fieldset__title">Cookie Categories</div>
                    {Object.entries(options.popup_options.cookies_groups).map(
                      ([groupId, group]) => (
                        <div key={groupId} className="field-row">
                          <div className="toggle-row">
                            <label className="checkbox big">
                              <input
                                type="checkbox"
                                checked={group.enable}
                                onChange={(e) =>
                                  handleOptionChange(
                                    `group_${groupId}_enable`,
                                    e.target.checked
                                  )
                                }
                                disabled={
                                  !instance?.subscription ||
                                  groupId === "group_1"
                                }
                              />
                              <div data-on="enabled" data-off="disabled">
                                <div></div>
                              </div>
                            </label>
                            <span>{group.name}</span>
                          </div>
                          <div className="field-row__description">
                            <ul>
                              {group.list.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-area__action">
          <button
            type="button"
            className="btn btn-primary btn-medium"
            onClick={handleSave}
            disabled={!instance?.subscription}
          >
            {instance?.subscription
              ? "Save Changes"
              : "Upgrade to Apply Changes"}
          </button>
        </div>
      </div>
    </ChakraProvider>
  );
}

export default App;
